/**
 * The frame every signed-in page renders inside.
 *
 * ONE COLUMN OF CHROME AND NO BAR ACROSS THE TOP. Sidebar on the left from
 * 1024px up, a slide-over below that, and the routed page beside it. The page
 * column is the only thing that scrolls, so the navigation and the brand's
 * colour never leave the screen.
 *
 * THE BAR IS NOT MISSING, IT WAS REHOMED. Seven things used to live up there
 * and every one of them has an address now: the brand switcher and the product
 * mark at the top of the sidebar, the palette's face under them, the person and
 * the theme at the bottom of it, the brand's colour rule down its outer edge,
 * and the mobile opener below.
 *
 * WHERE THE MOBILE OPENER WENT. Below 1024px, and only there, a slim bar runs
 * across the top: the menu button and the brand being worked in. It used to be
 * a floating button at the bottom left, chosen so it would not cover a page's
 * heading, but it sat on top of whatever the page drew there and the owner
 * asked for it at the top (2026-10-11). A bar in the flow, not a button laid
 * over the page, is how it can be at the top without covering the heading: the
 * page column starts below it. The desktop has no bar and is unchanged.
 */
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useMatches } from 'react-router-dom'
import { useShortcuts } from '../pages/queue/keyboard'
import { ErrorBoundary } from './ErrorBoundary'
import { CommandPalette } from './CommandPalette'
import { ShortcutsDrawer, ShortcutsOpener } from './ShortcutsDrawer'
import { useOverlayParam } from '../lib/url-state'
import { Sidebar } from './Sidebar'
import { LoadingState } from './States'
import { SystemBanner } from './SystemBanner'
import { useLocalStorage, useOnEscape } from '../lib/hooks'
import { cn } from '../lib/cn'
import { pageBrand } from '../lib/api'
import { invalidateBrandScopedQueries } from '../lib/queryClient'
import { useSession } from '../lib/session'
import { BrandMark } from './Identity'
import { collapsedStorageKey, LEGACY_COLLAPSED_KEY, readStoredFlag } from './sidebar-model'
import { IconMenu } from './icons'

/**
 * A page working in another brand (`?brand=`) reads that brand's data under
 * the same query keys the switcher's brand uses, so moving in or out of one
 * drops what was held, the way a switch does.
 *
 * Exported because the takeover layout renders pages outside this shell and
 * owes them the same guarantee.
 */
export function useBrandInAddressInvalidation(search: string): void {
  const brandInAddress = pageBrand(search)
  const previousBrand = useRef(brandInAddress)
  useEffect(() => {
    if (previousBrand.current === brandInAddress) return
    previousBrand.current = brandInAddress
    void invalidateBrandScopedQueries()
  }, [brandInAddress])
}

/**
 * Whether the screen asks to run to the edges of the pane.
 *
 * A route says so in its `handle` with `fullBleed: true`, beside its title, so
 * the page itself never has to undo the shell's padding with negative margins
 * that have to agree with numbers it cannot see. The deepest match decides,
 * as it does for the title. The padding stays below 1024px, where there is no
 * sidebar for the page to sit flush against.
 */
export function routeFullBleed(matches: readonly { handle?: unknown }[]): boolean {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const handle = matches[index]?.handle
    if (!handle || typeof handle !== 'object') continue
    const bleed = (handle as { fullBleed?: unknown }).fullBleed
    if (typeof bleed === 'boolean') return bleed
  }
  return false
}

/**
 * The pane's padding and width cap, and neither for a full-bleed screen.
 *
 * Full bleed is every screen drawn in the Page frame, which carries its own
 * gutter, and its band's rule has to reach both edges of the column at any
 * window width, as Salesforge's does. Two separate class lists rather than an
 * override: Tailwind emits `sm:px-6` after `sm:p-0`, so adding `p-0` on top
 * of the padded list lost to it and left a 24px gutter around the frame.
 */
export function shellPaneClass(fullBleed: boolean): string {
  return fullBleed
    ? 'flex h-full w-full flex-col'
    : 'mx-auto flex h-full w-full max-w-[100rem] flex-col px-4 py-5 sm:px-6 sm:py-6'
}

/**
 * What the fold was before it was kept per person, read once as the starting
 * value for somebody who has never folded the column under their own key. Wrapped
 * because storage can throw in a private window, and that is never a reason to
 * break the page.
 */
function legacyCollapsed(): boolean {
  try {
    return readStoredFlag(window.localStorage.getItem(LEGACY_COLLAPSED_KEY), false)
  } catch {
    return false
  }
}

export function AppShell() {
  const location = useLocation()
  const fullBleed = routeFullBleed(useMatches())
  useBrandInAddressInvalidation(location.search)
  const [navOpen, setNavOpen] = useState(false)
  // Per person, so two people sharing a machine each keep their own column.
  // `RequireAuth` has landed the session before this renders, so the key is
  // the person's from the first frame.
  const { user } = useSession()
  const [legacy] = useState(legacyCollapsed)
  const [collapsed, setCollapsed] = useLocalStorage(collapsedStorageKey(user?.id), legacy)

  useOnEscape(navOpen, () => setNavOpen(false))
  useEffect(() => setNavOpen(false), [location.pathname])

  /*
   * The two panels the shell owns, and the one place their keys are bound.
   *
   * Bound here rather than inside each panel so the shortcut works on every
   * screen without each one remembering to mount it, and so it keeps working
   * while the panel is closed, which is the only time it is any use.
   *
   * Both fire even while somebody is typing, which `useHotkeys` allows for
   * modified combinations exactly so a shortcut like these is reachable from
   * inside a search field.
   */
  const [paletteOpen, setPaletteOpen] = useState(false)
  // The drawer is `?sheet=shortcuts`, so a link opens it and Back closes it.
  const shortcuts = useOverlayParam('sheet', 'shortcuts')
  useShortcuts('app', {
    'app.palette': () => setPaletteOpen((was) => !was),
    'app.shortcuts': () => (shortcuts.isOpen ? shortcuts.close() : shortcuts.open()),
  })

  // Stable, because it is the context value every consumer of the opener sees
  // and a fresh function each render would re-render the whole sidebar for
  // nothing. `open` already keeps one identity, this just names it.
  const openShortcuts = useCallback(() => shortcuts.open(), [shortcuts.open])

  /*
   * Back to the top on a new page. The browser did this for free while the
   * document was what scrolled; now that the column scrolls instead, arriving
   * at a new screen would otherwise keep the offset of the one before it and
   * land somebody halfway down a page they have not seen.
   */
  const mainRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 })
  }, [location.pathname])


  /*
    ONE SCROLL CONTAINER, AND IT IS THE PAGE COLUMN.

    This used to be `min-h-screen`, so the DOCUMENT scrolled and every pane
    that wanted to fill the window had to work out how much window was left by
    measuring where it had landed. That is a question the DOM cannot answer
    reliably: a pane's top edge moves when a sibling ABOVE it grows, and no
    event fires for it. Proved on the work queue, where the account panel
    opening moved the list 170px down and not one of its eleven ancestors
    changed size, so neither a resize listener nor a ResizeObserver over the
    whole ancestor chain saw anything happen.

    Bounding the shell to the window turns that measurement into a layout
    question, which CSS answers for free: the column below has a definite
    height, so a pane inside it can simply take what is left. It also means
    there is exactly one scrollbar on screen instead of two.
  */
  return (
    <ShortcutsOpener onOpen={openShortcuts}>
      <div className="flex h-dvh flex-col overflow-hidden bg-canvas">
        <a href="#main" className="skip-link">
          Skip to content
        </a>

        {/*
          The brand's colour, on a phone.
          Below 1024px there is no sidebar to carry it, and the guard it provides,
          knowing which brand you are about to send from without reading a label,
          matters most on the screen where somebody is tapping rather than
          reading. Three pixels of colour with nothing in it is not the bar coming
          back: there is no control on it and nothing to hit.
        */}
        <SystemBanner />

        {/*
          The phone's bar: the way into the navigation, and which brand this is.
          In the flow above the page column, so it pushes the page down rather
          than covering its heading. Gone from 1024px, where the sidebar is.
        */}
        <MobileBar onOpen={() => setNavOpen(true)} />

        <div className="flex min-h-0 flex-1">
          {/*
            THE NAV SCROLLS ITSELF.

            Without a height bound this element is what made the document taller
            than the window: as a flex child it stretched to its content, and a
            nav longer than the viewport put a second scrollbar on EVERY page in
            the app, whatever was on it. Measured before this change, the document
            was 1128px on an 861px window and the sidebar was the whole reason.

            It needs no height of its own now. The shell is exactly the window and
            this row is what is left of it, so an ordinary stretched flex item is
            already the right size, and the overflow below is what keeps a long
            nav inside it. An earlier version of this pinned itself with
            `sticky` and `calc(100dvh - <the top bar>)`, which worked and was two
            numbers that had to agree with a layout they could not see.
          */}
          {/*
            THE BRAND'S COLOUR IS THIS COLUMN'S EDGE, and it replaces the hairline
            border rather than sitting beside it. With no top bar there is no rule
            under one to carry the hue, and an edge is the right place for it: it
            is the full height of the window, it is never scrolled away, and with
            no brand chosen the neutral tone paints the same divider the border
            used to. The aside is `relative` only so this can be absolute inside
            it, which is deliberately NOT a z-index: a stacking context here would
            trap the sidebar's two menus at this element's level, which is exactly
            how the old top bar's menu ended up painting under a table header.
          */}
          <aside
            className={cn(
              'relative hidden min-h-0 shrink-0 border-r border-line bg-surface-muted lg:flex lg:flex-col',
              collapsed ? 'w-sidebar-sm' : 'w-sidebar',
            )}
          >
            <Sidebar
              collapsed={collapsed}
              onToggleCollapsed={() => setCollapsed(!collapsed)}
            />
          </aside>

          {/*
            The same sidebar in the slide-over, not a reduced copy of it. It gets
            `onClose` instead of `onToggleCollapsed`, so the one icon at the top
            right of the column closes the panel here and folds it there, and the
            person at the bottom and the brand at the top come with it. Before
            this, the slide-over had a header reading "Navigate" and neither of
            those controls, so a phone had no way to switch brand or sign out.
          */}
          {navOpen ? (
            <div className="fixed inset-0 z-modal lg:hidden">
              <div className="absolute inset-0 animate-fade-in bg-black/70" onClick={() => setNavOpen(false)} />
              <div className="relative flex h-full w-sidebar max-w-[85vw] animate-slide-in-left flex-col border-r border-line bg-surface-muted shadow-xl">
                <Sidebar
                  onNavigate={() => setNavOpen(false)}
                  onClose={() => setNavOpen(false)}
                />
              </div>
            </div>
          ) : null}

          <main id="main" ref={mainRef} className="min-w-0 flex-1 overflow-y-auto">
            {/*
              `h-full` and not `min-h-full`, which is the difference between this
              working and not. A page that wants to fill the column says
              `flex-1 min-h-0` on its own root, and `flex-1` can only hand back
              space it knows the size of: against `min-h-full` the wrapper's
              height is still auto, so it grows to its content, so there is no
              free space to distribute, so the item takes its content height and
              the column overflows by however tall the content happened to be.
              Measured that way it overflowed by 27px. `h-full` makes the height
              definite and the same declaration then does what it reads as.

              Height, not max-height, so this still does the right thing for the
              pages that are long documents: their content simply exceeds the box
              and `main` scrolls it.
            */}
            <div data-full-bleed={fullBleed ? '' : undefined} className={shellPaneClass(fullBleed)}>
              <ErrorBoundary resetKey={location.pathname}>
                <Suspense fallback={<LoadingState label="Opening the page" />}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </div>
          </main>
        </div>

        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        {/* Mounted at the shell rather than per screen, so the key and the
            sidebar's Shortcuts link reach the same panel. */}
        <ShortcutsDrawer open={shortcuts.isOpen} onClose={shortcuts.close} />
      </div>
    </ShortcutsOpener>
  )
}

/** Below 1024px only: the menu button, then the brand being worked in. */
function MobileBar({ onOpen }: { onOpen: () => void }) {
  const { activeBrand } = useSession()
  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface-muted px-2 lg:hidden">
      <button
        type="button"
        onClick={onOpen}
        aria-label="Open navigation"
        title="Open navigation"
        className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-ink-muted transition hover:bg-surface-hover hover:text-ink-secondary"
      >
        <IconMenu className="h-5 w-5" />
      </button>
      {activeBrand ? (
        <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-ink">
          <BrandMark brandId={activeBrand.id} name={activeBrand.name} logoSvg={activeBrand.logoSvg} size="sm" />
          <span className="truncate">{activeBrand.name}</span>
        </span>
      ) : null}
    </header>
  )
}
